import type { SalesNearMatch } from '@/client/types/sales';
import { btnGhost, btnPrimary } from './sales-ui';

/** Shown on a `possible_duplicate` refusal: open the existing prospect, or
 *  confirm this is somebody else and add anyway. */
export function DuplicateMatchesDialog({
  name,
  matches,
  busy,
  onOpen,
  onConfirm,
  onCancel,
}: {
  name: string;
  matches: SalesNearMatch[];
  busy?: boolean;
  onOpen: (id: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dupe-title"
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-5 shadow-xl">
        <h3 id="dupe-title" className="text-base font-semibold text-gray-900">
          Already in the pool?
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          {name} looks like {matches.length === 1 ? 'this prospect' : 'these prospects'}. Open the existing one instead
          of adding a duplicate.
        </p>
        <ul className="mt-4 divide-y divide-gray-100 rounded-lg border border-gray-200">
          {matches.map((m) => (
            <li key={m.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">{m.displayName}</p>
                <p className="text-xs text-gray-500">
                  {[m.phone, m.areas?.[0]?.region, m.stage].filter(Boolean).join(' · ') || '—'}
                </p>
              </div>
              <button type="button" className={btnGhost} onClick={() => onOpen(m.id)}>
                Open
              </button>
            </li>
          ))}
        </ul>
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button type="button" className={btnGhost} onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className={btnPrimary} disabled={busy} onClick={onConfirm}>
            Different person — add anyway
          </button>
        </div>
      </div>
    </div>
  );
}
